import { useState, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { speakingAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const DRILL_SENTENCES = [
  { id: 1, text: 'She sells seashells by the seashore.', focus: 'S / SH sounds', ipa: '/ʃ/ vs /s/' },
  { id: 2, text: 'The thirty-three thieves thought that they thrilled the throne throughout Thursday.', focus: 'TH sound', ipa: '/θ/ vs /ð/' },
  { id: 3, text: 'How much wood would a woodchuck chuck if a woodchuck could chuck wood?', focus: 'W / CH sounds', ipa: '/w/ /tʃ/' },
  { id: 4, text: 'I would like to visit Vietnam and experience the vibrant culture.', focus: 'V / W distinction', ipa: '/v/ vs /w/' },
  { id: 5, text: 'The weather in winter is wonderful but quite cold.', focus: 'W sound & vowels', ipa: '/w/ /ɪ/ /ʌ/' },
]

export default function AISpeakingPronunciationDeepDivePage() {
  const [sentIdx, setSentIdx] = useState(0)
  const [phase, setPhase] = useState<'idle' | 'recording' | 'done'>('idle')
  const [seconds, setSeconds] = useState(0)
  const [result, setResult] = useState<any>(null)
  const [history, setHistory] = useState<{ sentence: string; score: number }[]>([])
  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const sent = DRILL_SENTENCES[sentIdx]

  const submit = useMutation({
    mutationFn: (blob: Blob) => {
      const fd = new FormData()
      fd.append('audio', blob, 'audio.webm')
      fd.append('meta', JSON.stringify({ partType: 'pronunciation_drill', questionPrompt: sent.text, sessionId: 'pronunciation-deep-dive' }))
      return speakingAPI.submit(fd)
    },
    onSuccess: (data) => {
      setResult(data)
      setPhase('done')
      const score = data.pronunciation?.pronunciationScore ?? 0
      setHistory(h => [...h, { sentence: sent.text, score }])
    },
  })

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      chunksRef.current = []
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data)
      recorder.onstop = () => { submit.mutate(new Blob(chunksRef.current, { type: 'audio/webm' })); stream.getTracks().forEach(t => t.stop()) }
      recorder.start()
      mediaRef.current = recorder
      setPhase('recording')
      setSeconds(0)
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } catch { alert('Không thể truy cập microphone.') }
  }

  function stopRecording() {
    mediaRef.current?.stop()
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }

  function nextSentence() {
    setPhase('idle'); setResult(null); setSeconds(0)
    setSentIdx(i => (i + 1) % DRILL_SENTENCES.length)
  }

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const avgScore = history.length > 0 ? history.reduce((a, b) => a + b.score, 0) / history.length : null

  return (
    <AppLayout>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#b41340', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>spatial_audio_off</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#b41340' }}>Pronunciation Deep Dive</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Luyện phát âm <span style={{ color: '#004be2', fontStyle: 'italic' }}>chuyên sâu</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Đọc to câu luyện tập, AI phân tích từng âm và chỉ ra điểm cần cải thiện.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 20rem', gap: '1.5rem', alignItems: 'start' }}>
        {/* Main panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Sentence selector */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {DRILL_SENTENCES.map((s, i) => (
                <button key={s.id} onClick={() => { setSentIdx(i); setPhase('idle'); setResult(null) }}
                  style={{ padding: '0.375rem 0.875rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.75rem', background: sentIdx === i ? '#004be2' : '#eef0ff', color: sentIdx === i ? 'white' : '#535b71' }}>
                  Câu {i + 1}
                </button>
              ))}
            </div>

            {/* Focus badge */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <span style={{ padding: '0.375rem 1rem', borderRadius: '9999px', background: 'rgba(180,19,64,0.1)', color: '#b41340', fontSize: '0.8125rem', fontWeight: 700 }}>
                🎯 {sent.focus}
              </span>
              <span style={{ padding: '0.375rem 1rem', borderRadius: '9999px', background: '#eef0ff', color: '#004be2', fontSize: '0.8125rem', fontWeight: 700, fontFamily: 'monospace' }}>
                {sent.ipa}
              </span>
            </div>

            {/* Sentence display */}
            <div style={{ background: 'linear-gradient(135deg, #004be2, #809bff)', borderRadius: '1.25rem', padding: '2rem', marginBottom: '1.5rem' }}>
              <H style={{ display: 'block', fontSize: '1.375rem', fontWeight: 800, color: 'white', lineHeight: 1.6, textAlign: 'center' }}>
                "{sent.text}"
              </H>
            </div>

            {/* Recording */}
            <div style={{ textAlign: 'center' }}>
              {phase === 'idle' && (
                <button onClick={startRecording}
                  style={{ padding: '1rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #b41340, #f74b6d)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(180,19,64,0.3)', display: 'inline-flex', alignItems: 'center', gap: '0.625rem' }}>
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>mic</span>
                  Đọc câu này
                </button>
              )}
              {phase === 'recording' && (
                <div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2rem', fontWeight: 900, color: '#b41340', marginBottom: '1rem' }}>{fmt(seconds)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '1.5rem', height: '3rem' }}>
                    {Array.from({ length: 18 }).map((_, i) => (
                      <div key={i} style={{ width: '0.25rem', borderRadius: '9999px', background: '#b41340', height: `${8 + Math.random() * 28}px`, animation: `pulse-wave ${0.7 + (i % 4) * 0.15}s ease-in-out infinite alternate` }} />
                    ))}
                  </div>
                  {submit.isPending ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', color: '#535b71' }}>
                      <div className="spinner" /> Đang phân tích phát âm...
                    </div>
                  ) : (
                    <button onClick={stopRecording}
                      style={{ padding: '1rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: '#272e42', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.625rem' }}>
                      <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>stop</span>
                      Dừng & Phân tích
                    </button>
                  )}
                </div>
              )}
              {phase === 'done' && (
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button onClick={() => { setPhase('idle'); setResult(null) }}
                    style={{ padding: '0.875rem 1.75rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer' }}>
                    Thử lại
                  </button>
                  <button onClick={nextSentence}
                    style={{ padding: '0.875rem 1.75rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #004be2, #809bff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem' }}>
                    Câu tiếp theo →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Deep dive result */}
          {phase === 'done' && result && (
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
              <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 800, marginBottom: '1.5rem' }}>Phân tích chi tiết từng từ</H>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(result.pronunciation?.wordScores ?? []).map((w: any, i: number) => {
                  const pct = Math.min(100, w.accuracyScore ?? 80)
                  const color = pct >= 80 ? '#004be2' : pct >= 60 ? '#b45309' : '#b41340'
                  const label = pct >= 80 ? 'Tốt' : pct >= 60 ? 'Khá' : 'Cần luyện'
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: `${color}06`, border: `1px solid ${color}20` }}>
                      <span style={{ fontWeight: 800, fontSize: '1rem', minWidth: '8rem', color: '#272e42' }}>{w.word}</span>
                      {w.phonemes && <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#535b71', minWidth: '6rem' }}>{w.phonemes}</span>}
                      <div style={{ flex: 1, height: '0.5rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '9999px', transition: 'width 0.6s' }} />
                      </div>
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: '0.9375rem', color, minWidth: '3rem', textAlign: 'right' }}>{pct.toFixed(0)}%</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color, minWidth: '4.5rem' }}>{label}</span>
                    </div>
                  )
                })}
              </div>
              {result.content?.feedback && (
                <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: '#eef0ff', borderRadius: '1rem' }}>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5', display: 'block', marginBottom: '0.5rem' }}>Gợi ý cải thiện</span>
                  <p style={{ fontSize: '0.9375rem', lineHeight: 1.7, color: '#272e42' }}>{result.content.feedback}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar: history + tips */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '5rem' }}>
          {/* Score history */}
          {history.length > 0 && (
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
              <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1rem' }}>Lịch sử luyện tập</H>
              {avgScore != null && (
                <div style={{ textAlign: 'center', padding: '1rem', background: '#eef0ff', borderRadius: '1rem', marginBottom: '1rem' }}>
                  <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: '#004be2' }}>{avgScore.toFixed(1)}</H>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#535b71', textTransform: 'uppercase' }}>Điểm TB</span>
                </div>
              )}
              {history.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#535b71', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Câu {i + 1}</span>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.875rem', color: h.score >= 8 ? '#004be2' : h.score >= 6 ? '#006571' : '#b41340' }}>{h.score.toFixed(1)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tips */}
          <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
            <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1rem', color: '#004be2' }}>Mẹo phát âm tốt</H>
            {[
              'Đọc chậm và rõ ràng từng âm',
              'Chú ý vị trí lưỡi và môi',
              'Nghe lại bản gốc trước khi đọc',
              'Luyện từng từ khó trước',
              'Ghi âm và so sánh với native speaker',
            ].map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.625rem', marginBottom: '0.75rem' }}>
                <span style={{ flexShrink: 0, width: '1.375rem', height: '1.375rem', borderRadius: '50%', background: '#004be2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 800 }}>{i + 1}</span>
                <span style={{ fontSize: '0.8125rem', lineHeight: 1.5, color: '#535b71' }}>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse-wave { from { height: 8px; opacity: 0.5; } to { height: 36px; opacity: 1; } }`}</style>
    </AppLayout>
  )
}
